import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { gradientPillCss } from '../styles/gradientPill';

const spaceshipDrift = keyframes`
  0% { transform: translateX(-50%) translate3d(-14px, 6px, 0); }
  25% { transform: translateX(-50%) translate3d(10px, -10px, 0); }
  50% { transform: translateX(-50%) translate3d(18px, 4px, 0); }
  75% { transform: translateX(-50%) translate3d(-8px, 14px, 0); }
  100% { transform: translateX(-50%) translate3d(-14px, 6px, 0); }
`;

const spaceshipTilt = keyframes`
  0% { transform: rotate(-3deg) scale(1); }
  25% { transform: rotate(1.5deg) scale(1.01); }
  50% { transform: rotate(3deg) scale(1.02); }
  75% { transform: rotate(-1deg) scale(1.008); }
  100% { transform: rotate(-3deg) scale(1); }
`;

const Hero = styled.section`
  position: relative;
  scroll-snap-align: start;
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 0 2rem;
  overflow: hidden;
  background-color: #eceaed;
  background-image:
    linear-gradient(
      90deg,
      rgba(246, 245, 247, 0.98) 0%,
      rgba(246, 245, 247, 0.96) 34%,
      rgba(246, 245, 247, 0.78) 56%,
      rgba(246, 245, 247, 0.42) 74%,
      rgba(246, 245, 247, 0.16) 100%
    ),
    url('/images/CybardHeroMaybe3.png'),
    linear-gradient(180deg, #f6f5f7 0%, #eceaed 100%);
  background-repeat: no-repeat, no-repeat, no-repeat;
  background-position: center center, right 5% center, center center;
  background-size: cover, min(56vw, 1320px) auto, cover;

 &::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1; /* above image, below content */

  background:
    /* bottom fade into next section color */
    linear-gradient(
      to bottom,
      rgba(239,236,240,0) 40%,
      rgba(239,236,240,0) 60%,
      rgba(239,236,240,0.65) 80%,
      rgba(239,236,240,1) 100%
    ),
    /* your existing vignette */
    radial-gradient(
      circle at 50% 50%,
      rgba(0, 0, 0, 0)     0%,
      rgba(0, 0, 0, 0)    0%,
      rgba(0, 0, 0, 0.45) 75%,
      rgba(0, 0, 0, 0.65) 100%
    );
}

  @media (max-width: 980px) {
    background-position: center center, right -6vw center, center center;
    background-size: cover, min(58vw, 620px) auto, cover;
  }

  @media (max-width: 767px) {
    min-height: auto;
    padding: 7rem 1.25rem 3rem;
    background-position: center center, center 68%, center center;
    background-size: cover, min(182vw, 820px) auto, cover;
  }
`;





const HeroInner = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 1800px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: clamp(1.5rem, 5vw, 5rem);

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 1.75rem;
  }
`;

const Copy = styled.div`
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;

  @media (min-width: 768px) {
    max-width: none;
  }

  @media (max-width: 767px) {
    align-items: center;
    text-align: center;
  }
`;

const Kicker = styled.p`
  margin: 0 0 1rem;
  font-size: clamp(1.05rem, 1.5vw, 1.8rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
  color: #1f2937;
`;

const Title = styled.h1`
  margin: 0 0 1.4rem;
  font-family: var(--font-future);
  font-size: clamp(4.25rem, 9vw, 11rem);
  font-weight: 700;
  line-height: 1.09;

  @media (min-width: 768px) {
    display: flex;
    align-items: baseline;
    gap: clamp(0.8rem, 1.8vw, 1.6rem);
    flex-wrap: wrap;
  }
`;

const TitleWord = styled.span`
  display: block;

  @media (min-width: 768px) {
    display: inline-block;
  }
`;

const TitleSolid = styled(TitleWord)`
  color: #1f2937;
`;

const TitleOutline = styled(TitleWord)`
  color: transparent;
  -webkit-text-stroke: 2px #1f2937;
  text-stroke: 2px #1f2937;

  @supports not (-webkit-text-stroke: 2px #1f2937) {
    color: rgba(255, 255, 255, 0.08);
    text-shadow:
      -1px 0 #1f2937,
      0 1px #1f2937,
      1px 0 #1f2937,
      0 -1px #1f2937;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 1.7rem;
  font-size: clamp(1rem, 2.2vw, 1.24rem);
  line-height: 1.6;
  font-weight: 700;
  max-width: 32ch;
  color: #334155;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  width: fit-content;
  ${gradientPillCss};
`;

const HeroVisual = styled.div`
  min-height: clamp(340px, 54vh, 680px);

  @media (max-width: 767px) {
    min-height: 220px;
  }
`;

const HeroSpaceshipWrap = styled.div`
  position: absolute;
  // top: clamp(6.5rem, 12vh, 9.25rem);
  left: 50%;
  width: min(34vw, 900px);
  max-width: 42%;
  pointer-events: none;
  user-select: none;
  z-index: 2;
  animation: ${spaceshipDrift} 11s ease-in-out infinite;

  @media (max-width: 980px) {
    width: min(42vw, 420px);
    top: clamp(6rem, 11vh, 8rem);
  }

  @media (max-width: 767px) {
    width: min(72vw, 360px);
    max-width: none;
    top: 12.5rem;
  }
`;

const HeroSpaceship = styled.img`
  display: block;
  width: 100%;
  height: auto;
  opacity: 0.96;
  filter: drop-shadow(0 14px 30px rgba(0, 0, 0, 0.18));
  transform-origin: center center;
  animation: ${spaceshipTilt} 6.5s ease-in-out infinite;
`;

export default function HeroSection() {
  return (
    <Hero id="hero">
      <HeroSpaceshipWrap aria-hidden="true">
        <HeroSpaceship src="/images/CybardSpaceship.png" alt="" />
      </HeroSpaceshipWrap>

      <HeroInner>
        <Copy>
          <Kicker>
            Digital services
            <br />
            for Legendary businesses
          </Kicker>

          <Title>
            <TitleSolid>Cybard</TitleSolid>
            <TitleOutline>Studio</TitleOutline>
          </Title>

          <Subtitle>
            Developing platforms, managing marketing and creating assets for world class companies
          </Subtitle>

          <CTAButton to="/services">
            Explore Our Services
          </CTAButton>
        </Copy>

        <HeroVisual aria-hidden="true" />
      </HeroInner>
    </Hero>
  );
}
